import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "./hrmsNotifications";
import Apis from "../../utils/apis";
import { API_AXIOS } from "../../http/interceptor";

export const upcomingEvents = createApi({
  reducerPath: "upcomingEvents",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["upcomingEvents"],
  endpoints: (builder) => ({
    getUpEvents: builder.query({
      query: ({ search }) => ({
        url: Apis.event + "/upcoming",
        params: {
          search,
        },
      }),
      providesTags: ["upcomingEvents"],
      keepUnusedDataFor: 300,
      transformResponse: (data) => data.data,
    }),
  }),
});

export const { useGetUpEventsQuery } = upcomingEvents;

export const eventsApi = createApi({
  reducerPath: "eventsList",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Events"],
  endpoints: (builder) => ({
    getEventsData: builder.query({
      queryFn: async ({
        page = 1,
        limit = 20,
        search,
        city,
        country,
        startDate,
        endDate,
      }) => {
        try {
          const { data } = await API_AXIOS.get(Apis.event, {
            params: {
              page,
              limit,
              search,
              city,
              country,
              startDate,
              endDate,
            },
          });
          return { data };
        } catch (error) {
          return { error: error.response?.data || error.message };
        }
      },
      providesTags: ["Events"],
      keepUnusedDataFor: 300,
    }),
    getEventAgents: builder.query({
      queryFn: async ({ eventId, page = 1, limit = 20, search }) => {
        if (!eventId) {
          return { data: { data: [], pagination: { totalItem: 0 } } };
        }

        try {
          const { data } = await API_AXIOS.get(`${Apis.event}/${eventId}/agents`, {
            params: { page, limit, search },
          });
          return { data };
        } catch (error) {
          return { error: error.response?.data || error.message };
        }
      },
      providesTags: ["Events"],
      keepUnusedDataFor: 60,
    }),
    getEventClients: builder.query({
      queryFn: async ({ eventId, page = 1, limit = 20, search, agentId }) => {
        try {
          const { data } = await API_AXIOS.get(`${Apis.event}/${eventId}/clients`, {
            params: { page, limit, search, agentId },
          });
          return { data };
        } catch (error) {
          return { error: error.response?.data || error.message };
        }
      },
      providesTags: ["Events"],
    }),
    sendEventLeadInvitation: builder.mutation({
      queryFn: async ({ eventId, eventLeadId }) => {
        try {
          const { data } = await API_AXIOS.post(`${Apis.event}/${eventId}/leads/${eventLeadId}/rsvp`);
          return { data };
        } catch (error) {
          return { error: error.response?.data || error.message };
        }
      },
      invalidatesTags: ["Events"],
    }),
    checkInEventLead: builder.mutation({
      queryFn: async ({ eventId, eventLeadId }) => {
        try {
          const { data } = await API_AXIOS.patch(`${Apis.event}/${eventId}/leads/${eventLeadId}/check-in`);
          return { data };
        } catch (error) {
          return { error: error.response?.data || error.message };
        }
      },
      invalidatesTags: ["Events"],
    }),
    updateEventLeadStatus: builder.mutation({
      queryFn: async ({ eventId, eventLeadId, ...status }) => {
        try {
          const { data } = await API_AXIOS.patch(
            `${Apis.event}/${eventId}/leads/${eventLeadId}/status`,
            status,
          );
          return { data };
        } catch (error) {
          return { error: error.response?.data || error.message };
        }
      },
      invalidatesTags: ["Events"],
    }),
  }),
});

export const {
  useGetEventsDataQuery,
  useGetEventAgentsQuery,
  useGetEventClientsQuery,
  useSendEventLeadInvitationMutation,
  useCheckInEventLeadMutation,
  useUpdateEventLeadStatusMutation,
} = eventsApi;

export const eventDetailsApi = createApi({
  reducerPath: "eventDetails",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["eventDetails"],
  endpoints: (builder) => ({
    getEventDetails: builder.query({
      query: (id) => ({
        url: `${Apis.event}/details/${id}`,
      }),
      providesTags: ["eventDetails"],
      keepUnusedDataFor: 300,
      transformResponse: (data) => data.data,
    }),
  }),
});

export const { useGetEventDetailsQuery } = eventDetailsApi;

export const upcomingEventApi = createApi({
  reducerPath: "upcomingEventApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["UpcomingEvent"],
  endpoints: (builder) => ({
    getUpcomingEvents: builder.query({
      query: () => ({
        url: Apis.getUpcomingEvents, // ex: /events/upcoming
        method: "GET",
      }),
      providesTags: ["UpcomingEvent"],
      transformResponse: (data) => data.data,

      // dashboard-friendly behavior
      pollingInterval: 5 * 60 * 1000, // 5 mins
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }),
  }),
});

export const { useGetUpcomingEventsQuery } = upcomingEventApi;
